import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPrintletCatalog, fetchPrintletCatalogSyncStatus, syncPrintletCatalog } from "../../api/PrintletAPIs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Package,
  RefreshCw,
  Search,
  Layers,
  Palette,
  MapPin,
  Inbox,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

function ProductCard({ product }) {
  const [expanded, setExpanded] = useState(false);
  const variants = product.variants || [];
  const printAreas = product.printAreas || [];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-sm transition">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
              <Package size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-800 truncate">{product.name}</h3>
              {product.description && (
                <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{product.description}</p>
              )}
            </div>
          </div>
          <span
            className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border ${
              product.status === "active"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-100 text-slate-500 border-slate-200"
            }`}
          >
            {product.status || "—"}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <Layers size={13} className="text-slate-400" />
            {variants.length} variant
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Palette size={13} className="text-slate-400" />
            {printAreas.length} print area
          </span>
          {product.externalId && (
            <span className="font-mono text-slate-400 truncate">{product.externalId}</span>
          )}
        </div>

        {(variants.length > 0 || printAreas.length > 0) && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? "إخفاء التفاصيل" : "عرض التفاصيل"}
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-4 grid md:grid-cols-2 gap-4">
          {variants.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">Variants</h4>
              <div className="flex flex-wrap gap-1.5">
                {variants.slice(0, 8).map((v) => (
                  <span
                    key={v.externalId}
                    className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-xs text-slate-600 font-mono"
                  >
                    {v.sku || v.externalId}
                  </span>
                ))}
                {variants.length > 8 && (
                  <span className="px-2 py-1 text-xs text-slate-400">+{variants.length - 8}</span>
                )}
              </div>
            </div>
          )}
          {printAreas.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-600 uppercase mb-2">Print Areas</h4>
              <div className="flex flex-wrap gap-1.5">
                {printAreas.slice(0, 8).map((a) => (
                  <span
                    key={a.externalId}
                    className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-xs text-slate-600"
                  >
                    {a.name}
                  </span>
                ))}
                {printAreas.length > 8 && (
                  <span className="px-2 py-1 text-xs text-slate-400">+{printAreas.length - 8}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PrintletCatalogPage() {
  const { data, refetch, isLoading, isFetching } = useQuery({
    queryKey: ["printletCatalog"],
    queryFn: fetchPrintletCatalog,
  });
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const sync = async () => {
    const requestedAt = Date.now();
    setSyncing(true);
    try {
      const started = await syncPrintletCatalog();
      toast.info(started?.message || "بدأت مزامنة الكتالوج في الخلفية...");
      const deadline = Date.now() + 10 * 60 * 1000;
      while (Date.now() < deadline) {
        await wait(2500);
        const status = await fetchPrintletCatalogSyncStatus();
        if (status?.running) continue;
        const completedAt = status?.completedAtUtc ? Date.parse(status.completedAtUtc) : 0;
        if (!status?.error && completedAt && completedAt < requestedAt - 1000) continue;
        if (status?.error) {
          toast.error(status.error);
        } else {
          toast.success(`تمت المزامنة: ${status?.result?.products || 0} منتج`);
        }
        await refetch();
        return;
      }
      toast.info("المزامنة ما زالت تعمل في الخلفية. حدّث الصفحة بعد قليل.");
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const products = data?.products || [];
  const cities = data?.cities || [];
  const filtered = search
    ? products.filter(
        (p) =>
          p.name?.toLowerCase().includes(search.toLowerCase()) ||
          p.externalId?.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  return (
    <div className="space-y-5">
      <ToastContainer position="top-center" />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Package size={22} className="text-indigo-600" />
            كتالوج Printlet
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {products.length} منتج · {cities.length} مدينة شحن
            {data?.lastCatalogSyncAtUtc
              ? ` · آخر مزامنة ${new Date(data.lastCatalogSyncAtUtc).toLocaleString("ar-EG")}`
              : ""}
          </p>
        </div>
        <button
          onClick={sync}
          disabled={syncing || isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition"
        >
          <RefreshCw size={16} className={syncing || isFetching ? "animate-spin" : ""} />
          مزامنة الكتالوج
        </button>
      </div>

      {/* Search */}
      {products.length > 0 && (
        <div className="relative">
          <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث في المنتجات..."
            className="w-full border border-slate-200 rounded-xl pr-9 pl-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition"
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="grid md:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4 h-32 animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && products.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-slate-200">
          <div className="p-4 rounded-full bg-slate-100 mb-3">
            <Inbox size={32} className="text-slate-400" />
          </div>
          <h3 className="font-bold text-slate-700 mb-1">لا يوجد منتجات</h3>
          <p className="text-sm text-slate-500 max-w-xs">
            اضغط على "مزامنة الكتالوج" لجلب المنتجات من Printlet.
          </p>
        </div>
      )}

      {/* Products grid */}
      {filtered.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          {filtered.map((p) => (
            <ProductCard key={p.externalId} product={p} />
          ))}
        </div>
      )}

      {/* Cities */}
      {cities.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
              <MapPin size={18} />
            </div>
            <h3 className="font-bold text-slate-800">مدن الشحن المتاحة</h3>
            <span className="mr-auto text-xs text-slate-500">{cities.length} مدينة</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {cities.map((c) => (
              <span
                key={c.externalId}
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50 transition"
              >
                {c.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}